/**
 * Phone numbers: `+998 90 123 45 67`.
 *
 * The services keep numbers in E.164 (`+998901234567`). A number of a country
 * this file knows is grouped the way that country writes it; anything else is
 * shown as it came, never guessed at.
 */
import { NBSP } from "./money";

interface Plan {
  /** Country calling code. */
  code: string;
  /** Sizes of the groups after the code. */
  groups: readonly number[];
}

const PLANS: readonly Plan[] = [
  { code: "998", groups: [2, 3, 2, 2] }, // Uzbekistan: +998 90 123 45 67
  { code: "7", groups: [3, 3, 2, 2] }, // Kazakhstan: +7 701 234 56 78
];

export function formatPhone(phone: string): string {
  const trimmed = phone.trim();
  if (!/^\+?[\d\s()-]+$/.test(trimmed)) return trimmed;
  const digits = trimmed.replace(/\D/g, "");
  for (const { code, groups } of PLANS) {
    const length = groups.reduce((sum, size) => sum + size, 0);
    if (!digits.startsWith(code) || digits.length !== code.length + length) continue;
    const parts: string[] = [];
    let at = code.length;
    for (const size of groups) {
      parts.push(digits.slice(at, at + size));
      at += size;
    }
    return [`+${code}`, ...parts].join(NBSP);
  }
  return trimmed;
}

/**
 * The calling code of the one market there is. A number typed the way people
 * say it at home (nine digits, no code) is taken to be of this country.
 */
export const MARKET_PHONE_CODE = "998";

/**
 * What a person typed as a phone, in E.164 (`+998901234567`), or null when
 * it cannot be one. Spaces, dashes and brackets do not matter; a number
 * without a country code gets the market's.
 */
export function parsePhone(input: string, countryCode: string = MARKET_PHONE_CODE): string | null {
  const trimmed = input.trim();
  if (!/^\+?[\d\s().-]+$/.test(trimmed)) return null;
  const digits = trimmed.replace(/\D/g, "");
  const plan = PLANS.find(({ code }) => code === countryCode);
  const local = plan?.groups.reduce((sum, size) => sum + size, 0);
  if (!trimmed.startsWith("+") && local !== undefined && digits.length === local) {
    return `+${countryCode}${digits}`;
  }
  return digits.length >= 8 && digits.length <= 15 ? `+${digits}` : null;
}
